import { Routes } from '@angular/router'

import { AuthGuard } from '@core/requests/guards/auth.guard'
import { NoCompetitionsGuard } from '@core/requests/guards/no-competitions.guard'
import { LoginGuard } from '@core/requests/guards/login.guard'
import { UpdateVersionGuard } from '@core/requests/guards/update-version.guard'
import { UserDisallowedGuard } from '@core/requests/guards/user-disallowed.guard'
import { GTMCategories } from '@core/enums/gtm.enums'
import { FirebaseAnalyticsRouteTitle } from '@core/services/analytics/firebase/enums/firebase-analytics.enums'
import { MetricFinderKey } from '@core/enums/metric.enums'
import { RouteUrl } from '@core/enums/routing.enums'
import { seasonsResolveFn } from '@features/seasons/resolver/seasons.resolver'
import { ErrorType } from '@pages/error-feedback/error-feedback.models'

export const routes: Routes = [
  {
    path: '',
    redirectTo: RouteUrl.Login,
    pathMatch: 'full'
  },
  {
    path: RouteUrl.Auth,
    loadChildren: () => import('./pages/auth/auth.routes').then((m) => m.routes)
  },
  {
    path: '',
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard, NoCompetitionsGuard, UpdateVersionGuard],
    children: [
      {
        path: RouteUrl.Main,
        loadChildren: () => import('./pages/main/main.routes').then((m) => m.routes)
      },
      {
        path: 'compare/seasons/:seasonId/competitions/:vsCompetitionId/teams',
        children: [
          {
            path: '',
            redirectTo: `/${RouteUrl.Home}`,
            pathMatch: 'full'
          },
          {
            path: ':vsTeamId',
            loadChildren: () => import('./pages/team/team.routes').then((m) => m.routes),
            data: {
              title: FirebaseAnalyticsRouteTitle.teamsComparison,
              gtmCategory: GTMCategories.TeamCompared,
              aggregationContext: MetricFinderKey.Team,
              callingContext: 'TeamSpace'
            }
          }
        ]
      },
      {
        path: RouteUrl.Players,
        loadChildren: () => import('./pages/player-detail/player-detail.routes').then((m) => m.routes)
      },
      {
        path: 'seasons/:seasonId/competitions/:competitionId/teams/:teamId/players',
        loadChildren: () => import('./pages/player-detail/player-detail.routes').then((m) => m.routes),
        data: {
          gtmCategory: GTMCategories.Player,
          callingContext: 'TeamSpace'
        },
        resolve: {
          seasonsInit: seasonsResolveFn
        }
      },
      {
        path: RouteUrl.Matches,
        loadChildren: () => import('./pages/matches/matches.routes').then((m) => m.routes),
        data: {
          callingContext: 'MatchesSpace'
        }
      },
      {
        path: RouteUrl.More,
        loadChildren: () => import('./pages/more/more.routes').then((m) => m.routes)
      },
      {
        path: RouteUrl.NotPermitted,
        loadChildren: () => import('./pages/error-feedback/error-feedback.routes').then((m) => m.routes),
        data: { errorType: ErrorType.Forbidden }
      },
      {
        path: RouteUrl.NetworkTimeout,
        loadChildren: () => import('./pages/error-feedback/error-feedback.routes').then((m) => m.routes),
        data: { errorType: ErrorType.Custom, message: 'MTR_NETWORK_TIMEOUT_ERROR' }
      },
      {
        path: RouteUrl.Notifications,
        loadChildren: () => import('./pages/notifications/notifications.routes').then((m) => m.routes)
      }
    ]
  },
  {
    path: RouteUrl.UpdateVersion,
    canActivate: [UpdateVersionGuard],
    loadChildren: () => import('./pages/error-feedback/error-feedback.routes').then((m) => m.routes),
    data: { errorType: ErrorType.UpdateVersion }
  },
  {
    path: RouteUrl.UserDisallowed,
    canActivate: [UserDisallowedGuard],
    loadChildren: () => import('./pages/error-feedback/error-feedback.routes').then((m) => m.routes),
    data: { errorType: ErrorType.UserDisallowed }
  },
  {
    path: RouteUrl.Concurrency,
    loadChildren: () => import('./pages/error-feedback/error-feedback.routes').then((m) => m.routes),
    data: { errorType: ErrorType.UserConcurrence }
  },
  {
    path: RouteUrl.Login,
    canActivate: [LoginGuard],
    loadChildren: () => import('./pages/login/login.routes').then((m) => m.routes)
  },
  {
    path: '**',
    loadChildren: () => import('./pages/error-feedback/error-feedback.routes').then((m) => m.routes),
    data: { errorType: ErrorType.NotFound }
  }
]
